import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { PaperPlaneRight, Sparkle, Globe, BookmarkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

function newSessionId() {
    return "sess_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

const SAMPLE_PROMPTS = [
    "How's my Adviser Portfolio doing vs the ASX 200 YTD?",
    "Show me my top spending categories over the last 3 months.",
    "Given my current savings rate, how long until I hit $10M net worth?",
    "What's the AUD/USD rate today?",
    "Where should I rebalance based on my target allocation?",
];

export default function Copilot() {
    const [sessionId] = useState(() => newSessionId());
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [useWeb, setUseWeb] = useState(true);
    const endRef = useRef(null);
    const [weeklyNote, setWeeklyNote] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    // If we arrive with ?prompt=…, prefill the input once and strip it from the URL
    useEffect(() => {
        const q = searchParams.get("prompt");
        if (q) {
            setInput(q);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Load the latest weekly portfolio sync note (if any) so the user sees it here
    useEffect(() => {
        api.get("/copilot/history?session_id=weekly-sync")
            .then(r => {
                const notes = r.data || [];
                if (notes.length) setWeeklyNote(notes[notes.length - 1]);
            })
            .catch(() => {});
    }, []);

    const send = async (textOverride) => {
        const text = (textOverride ?? input).trim();
        if (!text || busy) return;
        const userMsg = { role: "user", content: text };
        setMessages(m => [...m, userMsg]);
        setInput("");
        setBusy(true);
        try {
            const { data } = await api.post("/copilot/chat", {
                session_id: sessionId,
                message: text,
                use_web: useWeb,
            });
            setMessages(m => [...m, {
                role: "assistant",
                content: data.reply,
                web_used: data.web_used,
                web_results: data.web_results,
                question: text,
                saved: false,
            }]);
        } catch {
            setMessages(m => [...m, {
                role: "assistant",
                content: "Sorry — the copilot hit an error. Try again in a moment.",
            }]);
        } finally {
            setBusy(false);
        }
    };

    const saveInsight = async (idx, msg) => {
        try {
            await api.post("/ai/insight/save", {
                question: msg.question || "",
                answer: msg.content,
                session_id: sessionId,
            });
            setMessages(m => m.map((x, i) => i === idx ? { ...x, saved: true } : x));
            toast.success("Saved to AI Insights");
        } catch {
            toast.error("Failed to save");
        }
    };

    return (
        <div data-testid="copilot-page" className="flex flex-col" style={{ height: "calc(100vh - 5rem)" }}>
            <div className="mb-4">
                <div className="label-eyebrow mb-2">AI Copilot</div>
                <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Ask anything.</h1>
                <p className="text-sm mt-3 max-w-2xl" style={{ color: "var(--muted)" }}>
                    Claude Sonnet 4.5 with access to your app data and live web search
                    (DuckDuckGo). Compare your portfolio to markets, get spend insights,
                    or ask for scenario advice.
                </p>
            </div>

            <div className="flex-1 hairline bg-white rounded p-6 mb-4 overflow-y-auto"
                 data-testid="copilot-messages">
                {weeklyNote && messages.length === 0 && (
                    <div className="mb-6 p-4 rounded border-l-4"
                         style={{ borderColor: "var(--eucalyptus)", background: "var(--line)/20" }}
                         data-testid="weekly-sync-note">
                        <div className="text-[10px] uppercase tracking-widest mb-2"
                             style={{ color: "var(--eucalyptus)" }}>
                            Weekly system note
                        </div>
                        <div className="text-sm space-y-1">
                            {weeklyNote.content.split("\n").map((ln, i) => {
                                const trimmed = ln.replace(/^-\s?/, "").replace(/^\s{2}-\s?/, "");
                                const bold = trimmed.replace(/\*\*(.*?)\*\*/g,
                                    (_, t) => `<b>${t}</b>`);
                                const indent = ln.startsWith("  -") ? "ml-6" : ln.startsWith("-") ? "ml-3" : "";
                                const bullet = ln.trimStart().startsWith("-") ? "• " : "";
                                return (
                                    <div key={i} className={indent}
                                         dangerouslySetInnerHTML={{ __html: bullet + bold }} />
                                );
                            })}
                        </div>
                    </div>
                )}
                {messages.length === 0 && (
                    <div>
                        <div className="text-sm mb-4" style={{ color: "var(--muted)" }}>
                            Try one of these to get started:
                        </div>
                        <div className="grid md:grid-cols-2 gap-2">
                            {SAMPLE_PROMPTS.map((p, i) => (
                                <button key={i} onClick={() => send(p)}
                                        data-testid={`copilot-sample-${i}`}
                                        className="text-left text-sm p-3 rounded border border-[var(--line)] hover:bg-black/5 transition-colors">
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((m, i) => (
                    <div key={i} className={`mb-6 ${m.role === "user" ? "text-right" : ""}`}
                         data-testid={`copilot-msg-${i}`}>
                        <div className="text-[10px] uppercase tracking-widest mb-1"
                             style={{ color: "var(--muted)" }}>
                            {m.role === "user" ? "You" : "Copilot"}
                            {m.web_used && (
                                <span className="ml-2 inline-flex items-center gap-1">
                                    <Globe size={10} /> web
                                </span>
                            )}
                        </div>
                        <div className={`inline-block max-w-[80%] p-4 rounded text-sm text-left ${
                            m.role === "user"
                                ? "bg-[var(--eucalyptus)] text-[var(--bone)]"
                                : "bg-[var(--line)]/30"
                        }`}
                             style={{ whiteSpace: "pre-wrap" }}>
                            {m.content}
                        </div>
                        {m.web_results && m.web_results.length > 0 && (
                            <details className="mt-2 text-xs inline-block max-w-[80%] text-left"
                                     style={{ color: "var(--muted)" }}>
                                <summary className="cursor-pointer">
                                    {m.web_results.length} web sources
                                </summary>
                                <ul className="mt-1 space-y-1 pl-4">
                                    {m.web_results.slice(0, 5).map((r, ri) => (
                                        <li key={ri} className="list-disc">
                                            <a href={r.href} target="_blank" rel="noopener noreferrer"
                                               className="hover:underline">{r.title}</a>
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}
                        {m.role === "assistant" && m.question && (
                            <div className="mt-2">
                                <button
                                    onClick={() => saveInsight(i, m)}
                                    disabled={m.saved}
                                    data-testid={`copilot-save-${i}`}
                                    className="text-xs flex items-center gap-1 text-[var(--muted)] hover:text-[var(--eucalyptus)] disabled:opacity-50">
                                    <BookmarkSimple size={12} weight={m.saved ? "fill" : "regular"} />
                                    {m.saved ? "Saved to Insights" : "Save to Insights"}
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                {busy && (
                    <div className="text-sm flex items-center gap-2"
                         style={{ color: "var(--muted)" }}
                         data-testid="copilot-thinking">
                        <Sparkle size={14} className="animate-pulse" /> Thinking…
                    </div>
                )}
                <div ref={endRef} />
            </div>

            <div className="hairline bg-white p-4 rounded flex items-end gap-2"
                 data-testid="copilot-input-row">
                <label className="text-xs flex items-center gap-2 shrink-0"
                       style={{ color: "var(--muted)" }}>
                    <input type="checkbox" checked={useWeb}
                           onChange={e => setUseWeb(e.target.checked)}
                           data-testid="copilot-web-toggle" />
                    <Globe size={12} /> Web
                </label>
                <textarea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    placeholder="Ask about your finances or markets…"
                    rows={2}
                    disabled={busy}
                    data-testid="copilot-input"
                    className="flex-1 border border-[var(--line)] px-3 py-2 rounded text-sm resize-none"
                />
                <button onClick={() => send()} disabled={busy || !input.trim()}
                        className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
                        data-testid="copilot-send">
                    <PaperPlaneRight size={14} /> Send
                </button>
            </div>
        </div>
    );
}
